import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { Drawer } from 'vaul';
import FluidButton from 'ui/FluidButton';
import { TextInput } from 'ui/TextInput';
import { FilterChipGroup, IFilterChipOption } from 'ui/FilterChip';
import { CloseXComponent as CloseXIcon } from 'ui/Icons';
import { IHotelCollectionFilters } from './List';

interface IFiltersDrawerProps {
  className?: string;
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: IHotelCollectionFilters) => void;
  currentFilters: IHotelCollectionFilters;
  destinationOptions: IFilterChipOption[];
  bestForOptions: IFilterChipOption[];
}

export const FiltersDrawer = (props: IFiltersDrawerProps) => {
  // Temporary state for filters within the drawer
  const [tempName, setTempName] = useState('');
  const [tempCountryCodes, setTempCountryCodes] = useState<string[]>([]);
  const [tempBestFors, setTempBestFors] = useState<string[]>([]);

  // Sync temporary state with current filters when drawer opens
  useEffect(() => {
    if (props.isOpen) {
      setTempName(props.currentFilters.name);
      setTempCountryCodes(props.currentFilters.countryCodes);
      setTempBestFors(props.currentFilters.bestFors);
    }
  }, [props.isOpen, props.currentFilters]);

  const handleApply = () => {
    props.onClose();
    props.onApply({
      name: tempName,
      countryCodes: tempCountryCodes,
      bestFors: tempBestFors,
    });
  };

  const handleReset = () => {
    setTempName('');
    setTempCountryCodes([]);
    setTempBestFors([]);
    props.onClose();
    props.onApply({
      name: '',
      countryCodes: [],
      bestFors: [],
    });
  };

  return (
    <div className={classNames(props.className)}>
      <Drawer.Root direction="left" open={props.isOpen} onOpenChange={props.onClose} dismissible={true}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/40 z-[99998]" />
          <Drawer.Content className="h-[100dvh] bg-white p-[20px] w-[80%] max-w-[340px] fixed top-0 left-0 outline-none z-[99999]">
            <div className="relative flex flex-col h-full">
              {/* Header with close button */}
              <span className="font-pt-sans font-bold text-[17px] text-text-colour">Filter Results</span>

              <button
                onClick={props.onClose}
                className="absolute top-0 right-0 bg-transparent outline-none cursor-pointer p-0"
              >
                <CloseXIcon />
              </button>

              {/* Scrollable filter content */}
              <div className="flex-1 overflow-y-auto flex flex-col mt-20px gap-[20px]">
                {/* Search Input */}
                <TextInput
                  id="hotel-search-drawer"
                  value={tempName}
                  onChange={e => setTempName(e.currentTarget.value)}
                  placeholder="Search Hotel ..."
                />

                {/* Destination Filter */}
                {props.destinationOptions.length > 0 && (
                  <FilterChipGroup
                    label="DESTINATION"
                    options={props.destinationOptions}
                    selectedValues={tempCountryCodes}
                    onChange={setTempCountryCodes}
                  />
                )}

                {/* Best For Filter */}
                <FilterChipGroup
                  label="BEST FOR"
                  options={props.bestForOptions}
                  selectedValues={tempBestFors}
                  onChange={setTempBestFors}
                />
              </div>

              {/* Footer with Apply/Reset buttons */}
              <div className="flex gap-[10px] pt-5 pb-6">
                <FluidButton type="secondary" onClick={handleReset} className="flex-1">
                  Reset
                </FluidButton>
                <FluidButton type="primary" onClick={handleApply} className="flex-1">
                  Apply
                </FluidButton>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
};
